// SPDX-License-Identifier: AGPL-3.0-or-later

// LiveKit participant attribute the API writes with the worn character parts, next to micup.character; clients only
// read it. Clients that do not know it keep drawing micup.character.
export const ROOM_OUTFIT_ATTRIBUTE = 'micup.outfit';
export const ROOM_OUTFIT_ATTRIBUTE_MAX_BYTES = 512;

// Wire order, and the order in which a clash between two worn parts is settled (the earlier slot stays).
export const ROOM_OUTFIT_SLOTS = [
	'face',
	'hair',
	'top',
	'bottom',
	'shoes',
	'costume',
	'hat',
	'glasses',
	'accessory',
] as const;
export type RoomOutfitSlot = (typeof ROOM_OUTFIT_SLOTS)[number];

// One cosmetic item type per slot; the account wears it in users.room_<slot>_id.
export type RoomOutfitItemType = `room_${RoomOutfitSlot}`;
export const ROOM_OUTFIT_ITEM_TYPES: ReadonlyArray<RoomOutfitItemType> = ROOM_OUTFIT_SLOTS.map(
	(slot) => `room_${slot}` as const,
);

export function isRoomOutfitItemType(type: string): type is RoomOutfitItemType {
	return (ROOM_OUTFIT_ITEM_TYPES as ReadonlyArray<string>).includes(type);
}

// Skin regions of a family's base mesh (node skin_<region>); a part hides the regions it covers completely.
export const ROOM_BODY_REGIONS = [
	'head',
	'torso',
	'upper_arms',
	'lower_arms',
	'hands',
	'hips',
	'thighs',
	'shins',
	'feet',
] as const;
export type RoomBodyRegion = (typeof ROOM_BODY_REGIONS)[number];

// What a hat does to the hair under it; without an under-hat variant, under_hat hides the hair.
export type RoomHatHairRule = 'under_hat' | 'hide' | 'keep';

export interface RoomCharacterFamily {
	readonly base: string;
	readonly bytes: number;
	readonly clips: string;
	readonly regions: ReadonlyArray<RoomBodyRegion>;
	readonly default_preset: string;
}

export interface RoomCharacterPart {
	readonly family: string;
	readonly slot: RoomOutfitSlot;
	readonly file: string;
	readonly bytes: number;
	readonly tri: number;
	readonly hides: ReadonlyArray<RoomBodyRegion>;
	readonly excludes: ReadonlyArray<RoomOutfitSlot>;
	// Painted with the wearer's shirt colour.
	readonly personal: boolean;
	readonly hair?: RoomHatHairRule;
	readonly under_hat?: string;
	readonly head_top_add?: number;
	readonly preview: string;
	readonly license: string;
}

// A ready-made outfit; its key is also a room_character item's model_key.
export interface RoomCharacterPreset {
	readonly family: string;
	readonly parts: ReadonlyArray<string>;
	readonly shirt: number;
	readonly preview: string;
}

export interface RoomCharacterPartManifest {
	readonly default_family: string;
	readonly families: Readonly<Record<string, RoomCharacterFamily>>;
	readonly parts: Readonly<Record<string, RoomCharacterPart>>;
	readonly presets: Readonly<Record<string, RoomCharacterPreset>>;
}

export interface ResolvedRoomOutfitPart {
	readonly slot: RoomOutfitSlot;
	readonly key: string;
	// The GLB to draw: the hair's under-hat variant when a hat asks for it.
	readonly file: string;
}

export interface ResolvedRoomOutfit {
	readonly family: string;
	readonly preset: string;
	// Slot order; empty slots are absent.
	readonly parts: ReadonlyArray<ResolvedRoomOutfitPart>;
	// Base skin regions left out of the merge, in ROOM_BODY_REGIONS order.
	readonly hidden: ReadonlyArray<RoomBodyRegion>;
	readonly headTopAdd: number;
}

function own<T>(record: Readonly<Record<string, T>>, key: string | null | undefined): T | undefined {
	return key != null && Object.hasOwn(record, key) ? record[key] : undefined;
}

export function partsExclude(a: RoomCharacterPart, b: RoomCharacterPart): boolean {
	return a.excludes.includes(b.slot) || b.excludes.includes(a.slot);
}

// The manifest part an item of a room_<slot> type draws; null for a key that is not a part of that slot.
export function roomPartOf(
	manifest: RoomCharacterPartManifest,
	type: string,
	modelKey: string | null | undefined,
): RoomCharacterPart | null {
	const part = own(manifest.parts, modelKey);
	return part && `room_${part.slot}` === type ? part : null;
}

// The outfit a client draws and the dressing screen shows. Worn parts first (unknown keys and a second part for a
// taken slot drop alone; parts of more than one family all drop), then the preset fills the empty slots it can,
// then the family's default preset fills the required ones still empty (face, and top and bottom unless a costume is
// drawn). A part never goes on next to a part it excludes or that excludes it: a worn part beats a preset part, and
// of two worn parts the earlier slot stays. presetKey is the worn room character's model_key, when it is a preset.
export function resolveOutfit(
	manifest: RoomCharacterPartManifest,
	worn: Iterable<string>,
	presetKey?: string | null,
): ResolvedRoomOutfit {
	const wornBySlot = new Map<RoomOutfitSlot, string>();
	for (const key of worn) {
		const part = own(manifest.parts, key);
		if (part && !wornBySlot.has(part.slot)) wornBySlot.set(part.slot, key);
	}
	const wornFamilies = new Set([...wornBySlot.values()].map((key) => manifest.parts[key]!.family));
	if (wornFamilies.size > 1) wornBySlot.clear();
	const named = own(manifest.presets, presetKey);
	const family = wornBySlot.size > 0 ? [...wornFamilies][0]! : (named?.family ?? manifest.default_family);
	const fallback = manifest.families[family]!.default_preset;
	const preset = named?.family === family ? presetKey! : fallback;

	const drawn = new Map<RoomOutfitSlot, string>();
	const tryPut = (key: string) => {
		const part = own(manifest.parts, key);
		if (!part || part.family !== family || drawn.has(part.slot)) return;
		for (const other of drawn.values()) if (partsExclude(part, manifest.parts[other]!)) return;
		drawn.set(part.slot, key);
	};
	for (const slot of ROOM_OUTFIT_SLOTS) {
		const key = wornBySlot.get(slot);
		if (key) tryPut(key);
	}
	for (const key of manifest.presets[preset]!.parts) tryPut(key);
	const required = (slot: RoomOutfitSlot) =>
		slot === 'face' || ((slot === 'top' || slot === 'bottom') && !drawn.has('costume'));
	for (const key of manifest.presets[fallback]!.parts) {
		const part = own(manifest.parts, key);
		if (part && required(part.slot)) tryPut(key);
	}

	const hat = own(manifest.parts, drawn.get('hat'));
	const parts: Array<ResolvedRoomOutfitPart> = [];
	const hidden = new Set<RoomBodyRegion>();
	let headTopAdd = 0;
	for (const slot of ROOM_OUTFIT_SLOTS) {
		const key = drawn.get(slot);
		if (!key) continue;
		const part = manifest.parts[key]!;
		let file = part.file;
		if (slot === 'hair' && hat) {
			const rule = hat.hair ?? 'under_hat';
			if (rule === 'hide' || (rule === 'under_hat' && !part.under_hat)) continue;
			if (rule === 'under_hat') file = part.under_hat!;
		}
		parts.push({slot, key, file});
		for (const region of part.hides) hidden.add(region);
		headTopAdd = Math.max(headTopAdd, part.head_top_add ?? 0);
	}
	return {
		family,
		preset,
		parts,
		hidden: ROOM_BODY_REGIONS.filter((region) => hidden.has(region)),
		headTopAdd,
	};
}

const OUTFIT_ATTRIBUTE = /^1;[a-z0-9-]{1,32}(?:,[a-z0-9-]{1,32}){0,8}$/;

// '1;<key>,<key>…' in slot order (version 1); nothing worn gives ''.
export function formatOutfitAttribute(worn: Partial<Record<RoomOutfitSlot, string | null>>): string {
	const keys = ROOM_OUTFIT_SLOTS.map((slot) => worn[slot]).filter((key) => key != null);
	return keys.length > 0 ? `1;${keys.join(',')}` : '';
}

// The part keys, or null for anything but a well-formed version 1 value (an empty value included).
export function parseOutfitAttribute(value: string | undefined): ReadonlyArray<string> | null {
	if (value === undefined || value.length > ROOM_OUTFIT_ATTRIBUTE_MAX_BYTES || !OUTFIT_ATTRIBUTE.test(value)) {
		return null;
	}
	return value.slice(2).split(',');
}
